import React from "react";
import { SEO } from "@/components/SEO";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LocalizedLink } from "@/components/LocalizedLink";
import { 
  Zap, 
  Thermometer, 
  Droplets, 
  Shield, 
  Wifi, 
  Leaf, 
  Heart, 
  Sparkles,
  Settings,
  Eye,
  Wind,
  Brain
} from "lucide-react";

export default function Technology() {
  const technologies = [
    {
      icon: Thermometer,
      title: "Instant Heating System",
      description: "Advanced tankless heating technology provides immediate warm water without waiting. Energy-efficient design reduces power consumption while maintaining consistent temperatures.",
      features: ["No waiting time", "Energy efficient", "Precise temperature control"]
    },
    {
      icon: Droplets,
      title: "Smart Bidet Technology",
      description: "Multi-position nozzles with adjustable water pressure and temperature. Self-cleaning UV sterilization ensures maximum hygiene with every use.",
      features: ["UV sterilization", "Adjustable pressure", "Self-cleaning nozzles"]
    },
    {
      icon: Wind,
      title: "Warm Air Drying",
      description: "Integrated warm air drying system eliminates the need for toilet paper, providing comfort and reducing environmental impact.",
      features: ["Adjustable temperature", "Eco-friendly", "Complete comfort"]
    },
    {
      icon: Eye,
      title: "Motion Sensors",
      description: "Intelligent proximity sensors detect approach and departure, automatically opening and closing the lid for hands-free operation.",
      features: ["Auto open/close", "Hands-free operation", "Energy saving"]
    },
    {
      icon: Brain,
      title: "Smart Memory Settings",
      description: "Personalized user profiles store your preferred settings for water temperature, pressure, and seat heating. Up to 4 user profiles per unit.",
      features: ["4 user profiles", "One-touch recall", "Personalized experience"]
    },
    {
      icon: Shield,
      title: "Antimicrobial Protection",
      description: "Silver-ion infused surfaces and antimicrobial seat materials actively resist bacteria growth, ensuring lasting hygiene.",
      features: ["Silver-ion technology", "Bacteria resistant", "Easy maintenance"]
    },
    {
      icon: Leaf,
      title: "Eco-Friendly Design",
      description: "Water-saving flush technology uses up to 50% less water than traditional toilets. Energy-saving modes reduce power consumption during standby.",
      features: ["50% water savings", "Low power standby", "Sustainable materials"]
    },
    {
      icon: Sparkles,
      title: "Night Light System",
      description: "Soft LED illumination provides gentle guidance in the dark without harsh lighting, featuring multiple color options and brightness levels.",
      features: ["Multiple colors", "Motion activated", "Adjustable brightness"]
    }
  ];

  const certifications = [
    { name: "CE Certified", description: "European conformity standards" },
    { name: "IPX4 Waterproof", description: "Water-resistant electronics" },
    { name: "RoHS Compliant", description: "Restricted hazardous substances" },
    { name: "Energy Class A+", description: "High efficiency rating" }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Smart Toilet Technology | REM7 Engineering & Features"
        description="The engineering behind REM7 smart toilets: instant heating, bidet systems, motion sensors, antimicrobial protection and water-saving eco design."
        keywords="smart toilet technology, instant heating toilet, bidet technology, motion sensor toilet, antimicrobial toilet, eco-friendly bathroom, smart bathroom innovation"
        url="/technology"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center">
            <Badge variant="outline" className="border-accent/30 text-accent bg-accent/10 mb-6 text-lg px-4 py-2">
              Innovation & Engineering
            </Badge>
            <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl font-light mb-6 tracking-tight text-primary">
              Technology Behind REM7
            </h1>
            <p className="font-inter text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
              Every REM7 smart toilet integrates cutting-edge technology with elegant design. 
              Discover the innovations that make our products the choice of discerning customers across Europe.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 py-8">
        <BreadcrumbNav items={[
          { label: "Home", href: "/" },
          { label: "Technology" }
        ]} />
      </div>

      {/* Technology Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technologies.map((tech, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-border/50">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                    <tech.icon className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-playfair text-lg font-semibold text-primary mb-3">
                    {tech.title}
                  </h3>
                  <p className="font-inter text-sm text-muted-foreground mb-4 leading-relaxed">
                    {tech.description}
                  </p>
                  <ul className="space-y-2">
                    {tech.features.map((feature, idx) => (
                      <li key={idx} className="font-inter text-xs text-muted-foreground flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 px-4 sm:px-6 lg:px-12 bg-muted/30">
        <div className="container mx-auto max-w-7xl">
          <h2 className="font-playfair text-3xl font-medium text-primary text-center mb-12">
            Quality & Certifications
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="text-center p-6 bg-background rounded-xl border border-border/50">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-playfair font-semibold text-primary mb-2">{cert.name}</h3>
                <p className="font-inter text-sm text-muted-foreground">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-6">
            Experience the Technology
          </h2>
          <p className="font-inter text-lg text-muted-foreground mb-8">
            Ready to upgrade your bathroom with cutting-edge smart toilet technology?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90">
              <LocalizedLink to="/products/rem7">Explore REM7</LocalizedLink>
            </Button>
            <Button asChild size="lg" variant="outline">
              <LocalizedLink to="/contact">Contact Us</LocalizedLink>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
